/**
 * Double Row Template Parameters
 *
 * Defines form parameters and Zod schema for double-row hangars
 * (two rows of box units sharing a spine wall, back-to-back)
 */

import { z } from 'zod'
import type { BaseTemplateParams, DoorSpec, DoorType, UnitOverrideConfig, UnitDoorsConfig } from '../types'
import { DOOR_TYPE_DEFAULTS } from '../types'

// ============================================================================
// ENUMS & TYPES
// ============================================================================

/** Layout mode - symmetric mirrors inputs, custom allows independent config */
export type LayoutMode = 'symmetric' | 'custom'

/** Unit numbering style */
export type NumberingStyle = 'paired' | 'sequential' | 'custom'

/** Row configuration */
export interface RowConfig {
  /** Depth of units in this row (Y-axis) */
  depth_ft: number
  /** Number of units in this row */
  unit_count: number
}

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

/** Door specification schema (reused from single-row) */
export const doorSpecSchema = z.object({
  offset_ft: z.number().min(0, 'Offset must be non-negative'),
  width_ft: z.number().min(10, 'Door width must be at least 10 ft'),
  height_ft: z.number().min(8, 'Door height must be at least 8 ft'),
  type: z.enum(['bifold', 'sliding', 'hydraulic', 'rolling']),
})

/** Per-wall door configuration schema */
export const unitDoorsConfigSchema = z.object({
  front: z.array(doorSpecSchema).optional(),
  back: z.array(doorSpecSchema).optional(),
  left: z.array(doorSpecSchema).optional(),
  right: z.array(doorSpecSchema).optional(),
})

/** Unit override schema */
export const unitOverrideSchema = z.object({
  index: z.number().int().min(0),
  type: z.enum([
    'conventional_hangar', 'box_hangar', 't_hangar', 'shade',
    'tiedown', 'ramp', 'office', 'storage', 'shop', 'custom',
  ]).optional(),
  width_ft: z.number().min(15, 'Unit width must be at least 15 ft').optional(),
  doors: unitDoorsConfigSchema.optional(),
})

/** Row configuration schema */
export const rowConfigSchema = z.object({
  depth_ft: z.number().min(30, 'Row depth must be at least 30 ft').max(200),
  unit_count: z.number().int().min(1, 'At least 1 unit required').max(20, 'Max 20 units per row'),
})

/** Double row parameters schema */
export const doubleRowParamsSchema = z.object({
  // Base params
  name: z.string().min(1, 'Name is required'),
  code: z.string().min(1, 'Code is required').max(10, 'Code too long'),
  latitude: z.number(),
  longitude: z.number(),
  bearing_deg: z.number().transform(val => ((val % 360) + 360) % 360),
  floor_color: z.string(),
  wall_color: z.string(),

  // Layout mode
  layout_mode: z.enum(['symmetric', 'custom']),

  // Envelope dimensions
  width_ft: z.number().min(60, 'Width must be at least 60 ft').max(1000),
  height_ft: z.number().min(12, 'Height must be at least 12 ft').max(100),

  // Row configurations
  row_a: rowConfigSchema,
  row_b: rowConfigSchema,

  // Wall configuration
  shared_wall_thickness_ft: z.number().min(0.5).max(3).default(1.0),
  partition_wall_thickness_ft: z.number().min(0.25).max(2).default(0.5),

  // Default door configuration (applied to all units unless overridden)
  default_door_type: z.enum(['bifold', 'sliding', 'hydraulic', 'rolling']),
  default_door_width_ft: z.number().min(10).max(200),
  default_door_height_ft: z.number().min(8).max(50),

  // Per-unit overrides (per row)
  row_a_overrides: z.array(unitOverrideSchema).default([]),
  row_b_overrides: z.array(unitOverrideSchema).default([]),

  // Numbering style
  numbering_style: z.enum(['paired', 'sequential', 'custom']),
})

// ============================================================================
// TYPES
// ============================================================================

/** Double row template parameters interface */
export interface DoubleRowParams extends BaseTemplateParams {
  // Layout mode
  layout_mode: LayoutMode

  // Envelope dimensions
  width_ft: number
  height_ft: number

  // Row configurations
  row_a: RowConfig
  row_b: RowConfig

  // Wall configuration
  shared_wall_thickness_ft: number
  partition_wall_thickness_ft: number

  // Default door configuration
  default_door_type: DoorType
  default_door_width_ft: number
  default_door_height_ft: number

  // Per-unit overrides (per row)
  row_a_overrides: UnitOverrideConfig[]
  row_b_overrides: UnitOverrideConfig[]

  // Numbering style
  numbering_style: NumberingStyle
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/** Default values for double row template (symmetric mode) */
export const DEFAULT_DOUBLE_ROW_VALUES: DoubleRowParams = {
  name: 'New Double Row Hangar',
  code: 'DR',
  latitude: 0,
  longitude: 0,
  bearing_deg: 0,
  floor_color: '#808080',
  wall_color: '#A0A0A0',

  layout_mode: 'symmetric',

  width_ft: 300,
  height_ft: 26,

  row_a: {
    depth_ft: 90,
    unit_count: 4,
  },
  row_b: {
    depth_ft: 90,
    unit_count: 4,
  },

  shared_wall_thickness_ft: 1.0,
  partition_wall_thickness_ft: 0.5,

  default_door_type: 'bifold',
  default_door_width_ft: 60,
  default_door_height_ft: 22,

  row_a_overrides: [],
  row_b_overrides: [],

  numbering_style: 'paired',
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get effective row B config (mirrors row A in symmetric mode)
 */
export function getEffectiveRowB(params: DoubleRowParams): RowConfig {
  if (params.layout_mode === 'symmetric') {
    return { ...params.row_a }
  }
  return params.row_b
}

/**
 * Calculate total building depth from row configs
 */
export function calculateBuildingDepth(params: DoubleRowParams): number {
  const rowB = getEffectiveRowB(params)
  return params.row_a.depth_ft + rowB.depth_ft + params.shared_wall_thickness_ft
}

/**
 * Create default door specification from params
 */
export function createDefaultDoorSpec(params: DoubleRowParams, unitWidth: number): DoorSpec {
  // Center door in unit
  const doorWidth = Math.min(params.default_door_width_ft, unitWidth - 4) // Leave 2ft on each side

  return {
    offset_ft: (unitWidth - doorWidth) / 2,
    width_ft: doorWidth,
    height_ft: params.default_door_height_ft,
    type: params.default_door_type,
  }
}

/**
 * Get door specifications for a unit (respecting overrides)
 * Returns per-wall door configuration
 *
 * Both rows put doors on 'front' edge - the 180° rotation handles orientation:
 * - Row A (no rotation): front faces camera, doors visible from south
 * - Row B (180° rotation): front faces away from camera, doors on far side
 */
export function getUnitDoors(
  params: DoubleRowParams,
  rowId: 'A' | 'B',
  unitIndex: number,
  unitWidth: number,
  _unitCount: number
): UnitDoorsConfig {
  const overrides = rowId === 'A' ? params.row_a_overrides : params.row_b_overrides
  const override = overrides.find(o => o.index === unitIndex)

  // Both rows put doors on 'front' edge - the 180° rotation handles orientation:
  // - Row A (no rotation): front faces camera (doors visible)
  // - Row B (180° rotation): front faces away from camera (doors on far side)
  const exteriorWall = 'front'
  const sharedWall = 'back'

  if (override?.doors) {
    // Use override doors - all walls can have doors
    // - Front/back: always available
    // - Left/right: can be exterior (end units) or partition doors (interior units)
    const result: UnitDoorsConfig = {}

    // Copy all door configurations
    if (override.doors.front) {
      result[exteriorWall] = override.doors.front
    }
    if (override.doors.back) {
      result[sharedWall] = override.doors.back
    }
    if (override.doors.left) {
      result.left = override.doors.left
    }
    if (override.doors.right) {
      result.right = override.doors.right
    }

    // If no exterior doors specified, use default
    if (!result[exteriorWall] || result[exteriorWall]!.length === 0) {
      result[exteriorWall] = [createDefaultDoorSpec(params, unitWidth)]
    }
    return result
  }

  // Use default door on exterior wall (front for Row A, back for Row B)
  return {
    [exteriorWall]: [createDefaultDoorSpec(params, unitWidth)],
  } as UnitDoorsConfig
}

/**
 * Get door width constraints for door type
 */
export function getDoorConstraints(doorType: DoorType): { min: number; max: number } {
  const defaults = DOOR_TYPE_DEFAULTS[doorType]
  return {
    min: defaults.min_width_ft,
    max: defaults.max_width_ft,
  }
}

/**
 * Generate unit code for double row
 */
export function generateUnitCode(
  params: DoubleRowParams,
  rowId: 'A' | 'B',
  unitIndex: number,
  totalUnitsBeforeThisRow: number = 0
): string {
  const paddedIndex = String(unitIndex + 1).padStart(2, '0')

  switch (params.numbering_style) {
    case 'paired':
      // A-01, A-02, B-01, B-02 (paired across rows)
      return `${rowId}-${paddedIndex}`

    case 'sequential': {
      // 01, 02, 03... continuous across both rows
      const sequentialIndex = totalUnitsBeforeThisRow + unitIndex + 1
      return `${params.code}-${String(sequentialIndex).padStart(2, '0')}`
    }

    case 'custom':
      // Default to paired if custom not implemented
      return `${rowId}-${paddedIndex}`

    default:
      return `${rowId}-${paddedIndex}`
  }
}
